import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { Plan, Subscription } from '../types';
import toast from 'react-hot-toast';

const OFERTA_URL = 'https://iishet.ru/oferta/';

const formatRub = (kopecks: number): string =>
  (kopecks / 100).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽';

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ru-RU');
};

const Billing: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [payingCode, setPayingCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [promo, setPromo] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  const isSuccess = location.pathname.endsWith('/success');
  const isFail = location.pathname.endsWith('/fail');

  const loadSubscription = useCallback(async () => {
    try {
      const sub = await apiService.getSubscription();
      setSubscription(sub);
      return sub;
    } catch {
      setSubscription(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!apiService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const [p] = await Promise.all([apiService.getPlans(), loadSubscription()]);
        setPlans(p);
      } catch {
        toast.error('Не удалось загрузить тарифы');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, loadSubscription]);

  // После успешной оплаты подписку активирует webhook — опрашиваем, пока не станет active
  useEffect(() => {
    if (!isSuccess) return;
    let tries = 0;
    const id = setInterval(async () => {
      tries += 1;
      const sub = await loadSubscription();
      if ((sub && sub.active) || tries > 15) clearInterval(id);
    }, 2000);
    return () => clearInterval(id);
  }, [isSuccess, loadSubscription]);

  const handleRedeem = async () => {
    const code = promo.trim();
    if (!code) return;
    try {
      setRedeeming(true);
      const r = await apiService.redeemPromo(code);
      setSubscription(r.subscription);
      setPromo('');
      toast.success(r.message || 'Промокод активирован');
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Не удалось активировать промокод');
    } finally {
      setRedeeming(false);
    }
  };

  const handlePay = async (code: string) => {
    if (!accepted) {
      toast.error('Сначала примите условия публичной оферты');
      return;
    }
    try {
      setPayingCode(code);
      const { payment_url } = await apiService.checkout(code, true);
      window.location.href = payment_url;
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Не удалось перейти к оплате');
      setPayingCode(null);
    }
  };

  const sub = subscription;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Шапка */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <h1 className="text-xl font-bold text-gray-900">Тарифы и подписка</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
          >
            ← В кабинет
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Результат оплаты */}
        {isSuccess && (
          <div className="mb-6 rounded-lg border border-green-300 bg-green-50 p-4 text-green-800">
            <p className="font-semibold">Оплата получена ✅</p>
            <p className="text-sm mt-1">
              Подписка активируется автоматически после подтверждения платежа. Это может занять
              несколько секунд — статус обновится ниже.
            </p>
          </div>
        )}
        {isFail && (
          <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 text-red-800">
            <p className="font-semibold">Оплата не завершена</p>
            <p className="text-sm mt-1">Платёж не прошёл или был отменён. Можно попробовать ещё раз.</p>
          </div>
        )}

        {/* Текущая подписка */}
        <div className="mb-8 rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Текущая подписка
          </h2>
          {sub && sub.active ? (
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <div>
                <div className="text-gray-400">Тариф</div>
                <div className="font-medium text-gray-900">{sub.plan_name || '—'}</div>
              </div>
              <div>
                <div className="text-gray-400">Действует до</div>
                <div className="font-medium text-gray-900">{formatDate(sub.expires_at)}</div>
              </div>
              <div>
                <div className="text-gray-400">Заявок использовано</div>
                <div className="font-medium text-gray-900">
                  {sub.request_quota == null
                    ? `${sub.requests_used} (без лимита)`
                    : `${sub.requests_used} из ${sub.request_quota}`}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              Активной подписки нет. Выберите тариф ниже, чтобы получить доступ.
            </p>
          )}
        </div>

        {/* Промокод */}
        <div className="mb-8 rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Промокод
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <input
              value={promo}
              onChange={(e) => setPromo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRedeem(); }}
              placeholder="Введите промокод"
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm uppercase"
            />
            <button
              onClick={handleRedeem}
              disabled={redeeming || !promo.trim()}
              className="text-sm font-medium text-white bg-gray-800 hover:bg-gray-900 rounded-md px-4 py-2 disabled:opacity-50"
            >
              {redeeming ? 'Активация…' : 'Активировать'}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            Если у вас есть промокод на бесплатный доступ, введите его здесь.
          </p>
        </div>

        {/* Согласие с офертой */}
        <label className="flex items-start gap-2 mb-5 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <span>
            Я принимаю условия{' '}
            <a href={OFERTA_URL} target="_blank" rel="noreferrer" className="text-indigo-600 underline">
              публичной оферты
            </a>{' '}
            и согласен на обработку персональных данных.
          </span>
        </label>

        {/* Карточки тарифов */}
        {loading ? (
          <div className="text-gray-400">Загрузка тарифов…</div>
        ) : (
          <div
            className={`grid grid-cols-1 gap-5 ${
              plans.length >= 3 ? 'md:grid-cols-3' : plans.length === 2 ? 'md:grid-cols-2' : 'max-w-md'
            }`}
          >
            {plans.map((plan) => {
              const highlight = plans.length === 1 || plan.code === 'basic';
              return (
                <div
                  key={plan.id}
                  className={`rounded-xl border bg-white p-6 flex flex-col ${
                    highlight ? 'border-indigo-500 ring-1 ring-indigo-500 shadow-md' : 'border-gray-200'
                  }`}
                >
                  <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                  <div className="mt-2 text-3xl font-extrabold text-gray-900">
                    {formatRub(plan.price_kopecks)}
                  </div>
                  <div className="text-sm text-gray-500">за {plan.duration_days} дн.</div>
                  {plan.description && plan.description.includes('\n') ? (
                    // Описание из нескольких строк — список «что входит»
                    <ul className="mt-3 space-y-2 text-sm text-gray-600 flex-1">
                      {plan.description.split('\n').filter((line) => line.trim()).map((line) => (
                        <li key={line} className="flex gap-2">
                          <span className="text-indigo-500 font-bold">✓</span>
                          <span>{line.replace(/^•\s*/, '')}</span>
                        </li>
                      ))}
                    </ul>
                  ) : plan.description ? (
                    <p className="mt-3 text-sm text-gray-600 flex-1">{plan.description}</p>
                  ) : null}
                  <div className="mt-3 text-sm text-gray-700">
                    {plan.request_quota == null
                      ? 'Без лимита заявок в пределах срока'
                      : `До ${plan.request_quota} заявок`}
                  </div>
                  <button
                    onClick={() => handlePay(plan.code)}
                    disabled={!accepted || payingCode !== null}
                    className={`mt-5 w-full py-2 px-4 rounded-md text-sm font-medium text-white disabled:opacity-50 ${
                      highlight ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-800 hover:bg-gray-900'
                    }`}
                  >
                    {payingCode === plan.code ? 'Переход к оплате…' : 'Оплатить'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-6 text-xs text-gray-400">
          Оплата картой или через СБП. Оператор — ИП Кириченко М. А. Автопродление не подключается.
        </p>
      </div>
    </div>
  );
};

export default Billing;
